import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { Spin } from 'antd';

// removed useAllPolicyTransactions: using per-policy hook (usePolicyTransactionsByNumber) instead
import { usePolicyTransactionsByNumber } from "../../hooks/products/life/usePolicyTransactionsByNumber";
import { useAllLife } from '../../hooks/products/life/useAllLife';
import { getStatusMeta, normalizeStatusKey } from '../../util/policyStatus';
function TransactionsLifePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // (removed global policy transactions fetch) relying on per-policy fetch via usePolicyTransactionsByNumber

  // Also fetch all life policies (for additional context / future features)
  const { data: allLifeData } = useAllLife();
  React.useEffect(() => {
    if (allLifeData) {
      console.log('Fetched all life policies:', allLifeData);
    }
  }, [allLifeData]);

  // State for selected policy transactions
  const [selectedPolicyNumber, setSelectedPolicyNumber] = useState(null);
  const { data: selectedPolicyTransactions } = usePolicyTransactionsByNumber(
    selectedPolicyNumber, 
    !!selectedPolicyNumber
  );

  // Hold the product we're about to navigate with until transactions are ready
  const [pendingNavProduct, setPendingNavProduct] = useState(null);

  useEffect(() => {
    if (selectedPolicyTransactions) {
      console.log(`Transactions for policy ${selectedPolicyNumber}:`, selectedPolicyTransactions);
    }
  }, [selectedPolicyTransactions, selectedPolicyNumber]);

  const baseTabConfig = [
    { key: 'all', label: 'All' },
    { key: 'inforce', label: 'Inforce' },
    { key: 'maturity', label: 'Maturity' },
    { key: 'expired', label: 'Expired' },
    { key: 'paidup', label: 'Paid Up' },
    { key: 'surrendered', label: 'Surrendered' },
    { key: 'moved_to_das', label: 'Moved to DAS' },
    { key: 'surrendered_notified', label: 'Surrendered Notified' },
  ];

  const productTabs = baseTabConfig.map(tab => (
    tab.key === 'all'
      ? tab
      : { ...tab, tooltip: getStatusMeta(tab.key).description }
  ));

  const [activeTab, setActiveTab] = useState('all');
  const [hoveredTooltip, setHoveredTooltip] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, text: '', show: false });
  const tabsContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const container = tabsContainerRef.current;
    if (!container) return;

    const updateScrollState = () => {
      const el = tabsContainerRef.current;
      if (!el) return;
      setCanScrollLeft(el.scrollLeft > 8);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
    };

    updateScrollState();
    container.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);
    return () => {
      container.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, []);

  // Handle mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollTabs = (direction) => {
    const container = tabsContainerRef.current;
    if (!container) return;
    const amount = direction === 'left' ? -220 : 220;
    container.scrollBy({ left: amount, behavior: 'smooth' });
  };

  // Map API data to UI product structure
  const allProducts = useMemo(() => {
    // Prefer allLifeData.policy_details (same source as LifePage) when available
    if (Array.isArray(allLifeData?.data?.policy_details)) {
      return allLifeData.data.policy_details.map((p, idx) => ({
        id: p.policy_no || `life-${idx}`,
        type: p.plan_description?.toLowerCase().includes('investment') ? 'investment' : 'insure',
        name: p.plan_description || p.name || `Life Policy ${idx + 1}`,
        number: p.policy_no || `POL${(idx + 1).toString().padStart(6, '0')}`,
        status: p.status || '',
        policyNumber: p.policy_no || '',
        premiumAmount: p.modal_prem || p.premium_paid || 0,
        premiumFrequency: p.payment_frequency || '',
        totalPremiumPaid: p.premium_paid || 0,
        modalPremium: p.modal_prem || 0,
        totalRedemption: p.total_redemption || 0,
        startDate: p.issued_date || '',
        endDate: p.maturity_date || '',
        beneficiaries: Array.isArray(p.beneficiaries) ? p.beneficiaries : [],
        rawPolicyData: p
      }));
    }

    // No fallback to global policy transactions; return empty list if allLifeData not present
    return [];
  }, [allLifeData]);

  // Handle policy selection and transaction fetching
  const handlePolicyClick = async (product) => {
    try {
      setLoading(true);
      // Set the selected policy to trigger transaction fetch
      setSelectedPolicyNumber(product.policyNumber);
      // Save pending product + endpoint and wait for transactions to be fetched in an effect
      const transactionsEndpoint = `/policy-transaction?policy_number=${encodeURIComponent(product.policyNumber)}&month=&year=&amount=&reference=`;
      setPendingNavProduct({ product, transactionsEndpoint });
    } catch (error) {
      console.error('Failed to load policy transactions:', error);
      // Navigate anyway with current data
      setLoading(true);
      navigate('/life/transactions-history', { 
        state: { 
          productDetails: {
            policyName: product.name,
            policyNumber: product.policyNumber,
            status: product.status,
            totalPremiumPaid: product.totalPremiumPaid,
            modalPremium: product.modalPremium,
            premiumAmount: product.premiumAmount,
            premiumFrequency: product.premiumFrequency,
            totalRedemption: product.totalRedemption,
            startDate: product.startDate,
            endDate: product.endDate
          }, 
          beneficiaries: product.beneficiaries, 
          category: 'life'
        } 
      });
    }
  };

  // When pendingNavProduct is set, wait for selectedPolicyTransactions to be available
  // Then navigate with transactions included. Fallback: navigate after 2s even if transactions not ready.
  useEffect(() => {
    if (!pendingNavProduct) return;

    let didNavigate = false;
    const tryNavigate = (transactionsData) => {
      if (didNavigate) return;
      didNavigate = true;
      const { product, transactionsEndpoint } = pendingNavProduct;
      setLoading(true);
      navigate('/dashboard/life/transactions', {
        state: {
          productDetails: {
            policyName: product.name,
            policyNumber: product.policyNumber,
            status: product.status,
            totalPremiumPaid: product.totalPremiumPaid,
            modalPremium: product.modalPremium,
            premiumAmount: product.premiumAmount,
            premiumFrequency: product.premiumFrequency,
            totalRedemption: product.totalRedemption,
            startDate: product.startDate,
            endDate: product.endDate
          },
          beneficiaries: product.beneficiaries,
          category: 'life',
          transactionsEndpoint,
          // Prefer a raw array if available, otherwise pass the object so the page can handle both shapes
          transactions: (transactionsData && Array.isArray(transactionsData.data)) ? transactionsData.data : (Array.isArray(transactionsData) ? transactionsData : [])
        }
      });
      setPendingNavProduct(null);
    };

    // If transactions are already available for the selected policy, navigate immediately
    if (selectedPolicyTransactions && selectedPolicyTransactions.data) {
      tryNavigate(selectedPolicyTransactions);
      return;
    }

    // Otherwise wait up to 2 seconds for the transactions to arrive, then navigate with what's available
    const timeout = setTimeout(() => {
      tryNavigate(selectedPolicyTransactions || null);
    }, 2000);

    return () => clearTimeout(timeout);
  }, [pendingNavProduct, selectedPolicyTransactions, navigate]);

  const collapseStatusKey = (value) => normalizeStatusKey(value).replace(/\s+/g, '');

  // Filter products by tab
  const filteredProducts = activeTab === 'all'
    ? allProducts
    : allProducts.filter(p => collapseStatusKey(p.status).includes(collapseStatusKey(activeTab)));

  useEffect(() => {
    const el = tabsContainerRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      setCanScrollLeft(el.scrollLeft > 8);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
    });
  }, [activeTab, filteredProducts.length]);

  return (
    <div className="life-page-container" style={{
      padding: isMobile ? 8 : 16,
      width: '100%',
      boxSizing: 'border-box',
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', paddingTop: 16, width: '100%' }}>
        <div className="life-page-card" style={{
          background: '#fff',
          borderRadius: isMobile ? 16 : 28,
          boxShadow: '0 8px 40px 0 rgba(0,0,0,0.10)',
          padding: isMobile ? '16px 12px' : '32px 40px',
          minWidth: isMobile ? 280 : 320,
          maxWidth: 900,
          width: '100%',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 12
        }}>
          <h2 className="page-title" style={{ fontWeight: 700, color: '#222', marginBottom: 0, textAlign: 'left' }}>Life Insurance</h2>
          
          {/* Product Tabs */}
          <div
            className="tab-scroll-wrapper"
            style={{
              position: 'relative',
              width: '100%',
              marginBottom: 0,
              paddingTop: '20px',
              paddingBottom: '8px'
            }}
          >
            <button
              type="button"
              className="tab-scroll-btn"
              aria-label="Scroll tabs left"
              onClick={() => scrollTabs('left')}
              disabled={!canScrollLeft}
              style={{ left: 12, opacity: canScrollLeft ? 1 : 0 }}
            >
              <ChevronLeft size={18} color="#fff" />
            </button>
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              width: '100%',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              padding: '0 8px'
            }}
              className="tab-scrollbar-hide"
              ref={tabsContainerRef}
            >
            {productTabs.map(tab => (
              <div 
                key={tab.key} 
                className="tab-tooltip-wrapper" 
                style={{ position: 'relative' }}
                onMouseEnter={e => {
                  if (tab.tooltip) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setTooltipPosition({
                      x: rect.left + rect.width / 2,
                      y: rect.bottom + 12, // 12px below the tab
                      text: tab.tooltip,
                      show: true
                    });
                    setHoveredTooltip(tab.key);
                  }
                }}
                onMouseLeave={() => {
                  setHoveredTooltip(null);
                  setTooltipPosition(pos => ({ ...pos, show: false }));
                }}
              >
                <button
                  onClick={() => setActiveTab(tab.key)}
                  className="tab-button"
                  style={{
                    border: activeTab === tab.key ? 'none' : '2px solid #222',
                    background: activeTab === tab.key ? 'linear-gradient(90deg, #e6f9ed 0%, #b6f7d2 100%)' : '#fff',
                    color: activeTab === tab.key ? '#222' : '#222',
                    borderRadius: 999,
                    fontWeight: 600,
                    fontSize: 16,
                    padding: '8px 22px',
                    outline: 'none',
                    boxShadow: activeTab === tab.key ? '0 2px 8px 0 rgba(0,100,0,0.08)' : 'none',
                    cursor: 'pointer',
                    transition: 'all 0.18s',
                    borderWidth: activeTab === tab.key ? 0 : 2,
                    whiteSpace: 'nowrap',
                    marginBottom: 2,
                    marginRight: 12,
                  }}
                  tabIndex={0}
                  aria-pressed={activeTab === tab.key}
                  onFocus={() => {}}
                  onBlur={() => {}}
                >
                  {tab.label}
                </button>
                {/* Tooltip is now rendered via portal below */}
      {/* Tooltip Portal: Rendered outside tab section for visibility */}
      {tooltipPosition.show && tooltipPosition.text && hoveredTooltip && createPortal(
        <div
          className="tab-tooltip"
          style={{
            position: 'fixed',
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            transform: 'translate(-50%, 0)',
            background: 'linear-gradient(90deg, #e0faeaff 0%, #ffffffff 70%)',
            color: '#000000ff',
            padding: '18px 28px',
            borderRadius: '20px',
            fontSize: '13px',
            fontWeight: 700,
            whiteSpace: 'normal',
            maxWidth: '320px',
            textAlign: 'center',
            zIndex: 2147483647,
            boxShadow: '0 6px 5px rgba(0, 0, 0, 0.03)',
            pointerEvents: 'none',
            animation: 'tooltipFadeIn 0.2s ease',
            lineHeight: '1.4'
          }}
        >
          {tooltipPosition.text}
          <div style={{
            position: 'absolute',
            top: -8,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderBottom: '8px solid #b6f7d2'
          }} />
        </div>,
        document.body
      )}
              </div>
            ))}
            </div>
            <button
              type="button"
              className="tab-scroll-btn"
              aria-label="Scroll tabs right"
              onClick={() => scrollTabs('right')}
              disabled={!canScrollRight}
              style={{ right: 12, opacity: canScrollRight ? 1 : 0 }}
            >
              <ChevronRight size={18} color="#fff" />
            </button>
            <div className="tab-scroll-fade tab-scroll-fade-left" style={{ opacity: canScrollLeft ? 1 : 0 }} />
            <div className="tab-scroll-fade tab-scroll-fade-right" style={{ opacity: canScrollRight ? 1 : 0 }} />
          </div>

          {/* Products List */}
          <div style={{ width: '100%' }}>
            {filteredProducts.length === 0 ? (
              <div style={{ 
                color: '#888', 
                fontSize: 16, 
                margin: '12px 0 24px 0',
                textAlign: 'center',
                padding: '40px 20px',
                background: '#f8f9fa',
                borderRadius: '12px',
                border: '2px dashed #ddd'
              }}>
                No products found for the selected category.
              </div>
            ) : (
              <>
                <div style={{ 
                  color: '#666', 
                  fontSize: 14, 
                  marginBottom: 0,
                  fontWeight: 500,
                  paddingLeft: 4
                }}>
                  {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} found
                </div>
                {filteredProducts.map(product => (
                  <div
                    key={product.id}
                    className="insure-row-hover product-row"
                    tabIndex={0}
                    role="button"
                    aria-label={`Go to ${product.name}`}
                    onClick={() => handlePolicyClick(product)}
                    onKeyDown={e => { 
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handlePolicyClick(product);
                      }
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                      <span className="product-number">{product.number}</span>
                      <span className="product-name">{product.name}</span>
                    </div>
                    <ChevronRight style={{ fontSize: 26, color: '#bbb', marginLeft: 24 }} size={26} />
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Enhanced Styles */}
        <style>{`
          .tab-scrollbar-hide::-webkit-scrollbar { 
            display: none; 
          }
          .tab-scrollbar-hide { 
            -ms-overflow-style: none; 
            scrollbar-width: none; 
          }
          .tab-scroll-wrapper {
            position: relative;
          }
          .tab-scroll-btn {
            position: absolute;
            top: calc(50% - 12px);
            transform: translateY(-50%);
            width: 44px;
            height: 44px;
            border-radius: 50%;
            border: none;
            background: #00b050;
            box-shadow: 0 10px 25px rgba(0, 176, 80, 0.35);
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: opacity 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
            z-index: 50;
            color: #fff;
          }
          .tab-scroll-btn:disabled {
            opacity: 0;
            cursor: default;
            box-shadow: none;
            pointer-events: none;
          }
          .tab-scroll-btn:hover:not(:disabled) {
            transform: translateY(-50%) scale(1.08);
            box-shadow: 0 12px 28px rgba(0, 176, 80, 0.45);
          }
          @media (max-width: 768px) {
            .tab-scroll-btn {
              display: none !important;
            }
            .tab-scroll-wrapper {
              padding-top: 0 !important;
              margin-top: 0 !important;
            }
          }
          .tab-scroll-fade {
            position: absolute;
            top: 0;
            bottom: 0;
            width: 48px;
            pointer-events: none;
            transition: opacity 0.2s ease;
          }
          .tab-scroll-fade-left {
            left: 0;
            background: linear-gradient(90deg, #fff 0%, rgba(255,255,255,0));
          }
          .tab-scroll-fade-right {
            right: 0;
            background: linear-gradient(270deg, #fff 0%, rgba(255,255,255,0));
          }
          
          .tab-tooltip-wrapper { 
            position: relative; 
            display: inline-flex; 
            vertical-align: top; 
            z-index: 20; 
          }
          
          @keyframes tooltipFadeIn {
            from {
              opacity: 0;
              transform: translateX(-50%) translateY(-5px);
            }
            to {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }
          
          .insure-row-hover:hover, .insure-row-hover:focus {
            background: linear-gradient(90deg, #e6f9ed 0%, #d2f7e3 100%) !important;
            border-color: #b6f7d2 !important;
            outline: none !important;
          }
          .insure-row-hover:focus {
            box-shadow: 0 0 0 2px rgba(182, 247, 210, 0.5) !important;
          }
          /* Divider and responsive typography for life list */
          .product-row {
            display: flex;
            align-items: center;
            width: 100%;
            min-height: 70px;
            border-radius: 14px;
            padding: 12px 18px;
            margin-bottom: 0;
            cursor: pointer;
            transition: background 0.18s;
            border: 1px solid transparent;
            border-bottom: 1px solid #eee;
            background-clip: padding-box;
          }
          .product-row:last-child { border-bottom: none; }

          .product-number { color: #888; font-weight: 500; font-size: 15px; letter-spacing: 1; margin-bottom: 2px; line-height: 1.1; }
          .product-name { font-weight: 500; font-size: 21px; color: #111; line-height: 1.2; }

          /* Responsive adjustments */
          @media (max-width: 900px) {
            .product-row { min-height: 64px; padding: 10px 14px; }
            .product-name { font-size: 18px !important; }
            .product-number { font-size: 13px !important; }
          }

          /* Mobile responsive padding for main card */
          @media (max-width: 768px) {
            .life-page-card {
              padding: 32px 24px !important;
            }
            .life-page-container {
              padding: 12px !important;
            }
          }
          
          @media (max-width: 520px) {
            .life-page-card {
              padding: 24px 16px !important;
            }
            .life-page-container {
              padding: 8px !important;
            }
          }
          
          /* Ultra mobile responsive */
          @media (max-width: 600px) {
            .life-page-container {
              padding: 4px !important;
              padding-top: 8px !important;
              width: 100% !important;
              box-sizing: border-box !important;
            }
            .life-page-card {
              padding: 12px 8px !important;
              border-radius: 12px !important;
              max-width: none !important;
              min-width: auto !important;
              width: 100% !important;
              box-sizing: border-box !important;
            }
            .tab-button {
              margin-right: 6px !important;
              padding: 6px 12px !important;
              font-size: 14px !important;
            }
            .product-row { min-height: 60px; padding: 8px 12px; }
            .product-name { font-size: 12px !important; }
            .product-number { font-size: 9px !important; }
          }
        `}</style>
        
        {/* Loading Overlay */}
        {loading && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}>
            <Spin size="large" tip="Loading..." />
          </div>
        )}
      </div>
    </div>
  );
}

export default TransactionsLifePage;
